/**
 * Forge list + lifecycle mutations (ADR-023).
 *
 * React Query owns the server state; the list refetches on a short interval
 * whenever any forge is in a transitional status so a "Starting…" chip
 * resolves without a manual refresh.
 */
import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { forgeQueryKeys } from '../api/queryKeys';
import {
  FORGE_TRANSITIONAL_STATUSES,
  type ForgeSize,
  type ForgeSummary,
  type SharedForgeSummary,
} from '../types';

const LIST_POLL_MS = 5_000;

export interface UseForgesResult {
  forges: ForgeSummary[];
  /**
   * Forges of arches the user is an active collaborator on (any role) —
   * read-only, for opening the team's test build. Empty unless the hook was
   * asked for `includeShared`.
   */
  shared: SharedForgeSummary[];
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
  /** `size` defaults to standard on the server; `large` is Pro-only (403 surfaces via `error`). */
  createForge: (name: string, size?: ForgeSize) => Promise<ForgeSummary>;
  isCreating: boolean;
  /**
   * Forge from a CLI-published plugin's last submitted source (ADR-023 D6).
   * The caller must be the arche's contributor; the CLI stays a valid path.
   */
  importFromPlugin: (archeId: string) => Promise<ForgeSummary>;
  isImporting: boolean;
  startForge: (forgeId: string) => Promise<void>;
  stopForge: (forgeId: string) => Promise<void>;
  /** The forge a start/stop is in flight for (drives per-card spinners). */
  pendingForgeId: string | null;
}

function errorMessage(err: unknown): string | null {
  if (!err) return null;
  return err instanceof Error ? err.message : String(err);
}

export function useForges(options?: {
  includeArchived?: boolean;
  includeShared?: boolean;
}): UseForgesResult {
  const includeArchived = options?.includeArchived ?? false;
  const includeShared = options?.includeShared ?? false;
  const queryClient = useQueryClient();
  const [pendingForgeId, setPendingForgeId] = useState<string | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: forgeQueryKeys.list(
      includeShared ? { includeArchived, includeShared } : { includeArchived },
    ),
    queryFn: () =>
      includeShared
        ? api.listForges({ includeArchived, includeShared })
        : api.listForges({ includeArchived }),
    refetchInterval: (query) => {
      const forges = query.state.data?.forges ?? [];
      return forges.some((f) => FORGE_TRANSITIONAL_STATUSES.has(f.status)) ? LIST_POLL_MS : false;
    },
  });

  const invalidateLists = useCallback(
    () => queryClient.invalidateQueries({ queryKey: forgeQueryKeys.lists() }),
    [queryClient],
  );

  const createMutation = useMutation({
    mutationFn: (input: { name: string; size?: ForgeSize }) => api.createForge(input),
    onSuccess: () => {
      setMutationError(null);
      void invalidateLists();
    },
    onError: (err) => setMutationError(errorMessage(err)),
  });

  const importMutation = useMutation({
    mutationFn: (archeId: string) => api.importForgeFromPlugin(archeId),
    onSuccess: () => {
      setMutationError(null);
      void invalidateLists();
    },
    onError: (err) => setMutationError(errorMessage(err)),
  });

  const lifecycleMutation = useMutation({
    mutationFn: ({ forgeId, action }: { forgeId: string; action: 'start' | 'stop' }) =>
      action === 'start' ? api.startForge(forgeId) : api.stopForge(forgeId),
    onMutate: ({ forgeId }) => setPendingForgeId(forgeId),
    onSuccess: (result) => {
      setMutationError(null);
      // Pessimistic: the response IS the new row; drop it into the list and
      // the detail cache so the chip flips before the refetch lands.
      queryClient.setQueryData<{ forge: ForgeSummary }>(
        forgeQueryKeys.detail(result.forge.id),
        (prev) => ({ ...(prev ?? {}), forge: result.forge }),
      );
      void invalidateLists();
    },
    onError: (err) => setMutationError(errorMessage(err)),
    onSettled: () => setPendingForgeId(null),
  });

  const forges = useMemo(() => listQuery.data?.forges ?? [], [listQuery.data]);
  const shared = useMemo(() => listQuery.data?.shared ?? [], [listQuery.data]);

  const createForge = useCallback(
    async (name: string, size?: ForgeSize) =>
      (await createMutation.mutateAsync(size ? { name, size } : { name })).forge,
    [createMutation],
  );
  const importFromPlugin = useCallback(
    async (archeId: string) => (await importMutation.mutateAsync(archeId)).forge,
    [importMutation],
  );
  const startForge = useCallback(
    async (forgeId: string) => {
      await lifecycleMutation.mutateAsync({ forgeId, action: 'start' });
    },
    [lifecycleMutation],
  );
  const stopForge = useCallback(
    async (forgeId: string) => {
      await lifecycleMutation.mutateAsync({ forgeId, action: 'stop' });
    },
    [lifecycleMutation],
  );

  return {
    forges,
    shared,
    isLoading: listQuery.isLoading,
    error: mutationError ?? errorMessage(listQuery.error),
    refresh: () => void listQuery.refetch(),
    createForge,
    isCreating: createMutation.isPending,
    importFromPlugin,
    isImporting: importMutation.isPending,
    startForge,
    stopForge,
    pendingForgeId,
  };
}
